import { Flex } from '@chakra-ui/react';
import { useGoalsWeight } from 'components/GoalsWeight/GoalsWeightContext';
import { openPanel } from 'components/RabbitInitiativeCreationPanel/InitiativeCreationPanelSlice';
import { closeSecondaryPanel } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { CREATE_INITIATIVE_PRIVILEGES } from 'components/hooks/useEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import { okrsPageSelector } from 'selectors/okrsPage';
import store from 'store';
import { check1on1V2Enabled, checkDenyPublicCFVisibilityEnabled } from 'utils/helpers/featureFlags';
import {
  checkGoalModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkReviewModuleEnabled,
  checkTaskModuleEnabled,
  checkFeedbackModuleEnabled,
} from 'utils/helpers/moduleAccess';
import { checkUserIsReviewAdmin, checkUserIsSelf } from 'utils/helpers/rolesAccess';

import {
  ChangeWeightage,
  Initiatives,
  OneOnOne,
  Praise,
  RequestFeedback,
  ShareFeedback,
} from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';

import { FEEDBACK_FORM_TYPE, OKR_TYPES } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import {
  sendOpenedInitiativePanel,
  sendCreatedKREvent,
  sendOpenedKRPanelEvent,
  sendPageViewEvent,
  sendClickedOnQuickActionsEvent,
} from 'constants/mixpanelHelper';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

const ChangeWeightageComp = ({ employeeId, withPadding = false }) => {
  const { showSidePanel, hideSidePanel } = useGoalsWeight();

  const { moduleName } = useOrganisationSettings();

  const openMyWeightageSidePanel = () => {
    showSidePanel({
      employeeId,
      canEdit: true,
      onCancel: () => {
        hideSidePanel();
      },
      onSuccess: () => {
        hideSidePanel();
      },
    });
  };

  return (
    <Flex
      alignItems="center"
      // conditional to support both vertical and horizontal layout of quick actions
      px={withPadding ? 3 : 0}
      py={withPadding ? 2 : 0}
      mb={withPadding ? 0 : 3}
      minW="160px"
      gap={2}
      cursor="pointer"
      onClick={() => {
        sendClickedOnQuickActionsEvent({
          eventProperties: {
            action: 'Change Weightage',
          },
        });
        openMyWeightageSidePanel();
      }}
    >
      <ChangeWeightage boxSize="24px" color="text.g2" fill="transparent" />
      <TextBodyMedium color="text.g1">Manage {moduleName} weightage</TextBodyMedium>
    </Flex>
  );
};

export const QUICK_ACTIONS_LIST = [
  /* 
    TODO: add ids. lists ideally should have ids to target a specific item. 
      displayName can be used for targeting but renaming become costly effort.
    cc: @shreeyansh-mesh
    */
  {
    displayName: 'Create a Goal',
    Icon: KeyResult,
    isActionVisible: checkGoalModuleEnabled,
    onClick: ({ history, location, employee, dispatch }) => {
      const { selectedTimelines } = store.getState(okrsPageSelector);
      sendOpenedKRPanelEvent({
        eventProperties: { page: 'Home', source: 'Quick Actions', type: 'Add KR Panel' },
      });
      sendPageViewEvent({
        eventProperties: {
          page: 'Add KR Panel',
          type: 'Panel',
          source: 'Quick Actions',
        },
      });
      dispatch(
        openCreateOKRPanel({
          selectedObjective: null,
          selectedTimeBoundPeriod: selectedTimelines?.[0],
          okrPanelType: OKR_TYPES.KEY_RESULT,
          ownerEmployee: employee,
          onKeyResultCreation: (okrDetails) => {
            sendCreatedKREvent({
              eventProperties: { goalId: okrDetails?.uuid, source: 'Quick Actions', page: 'Home' },
            });
          },
          onObjectiveCreation: () => {},
          history,
          location,
        })
      );
    },
  },
  {
    displayName: 'Manage goal weightage',
    Icon: ChangeWeightage,
    isActionVisible: () => {
      if (checkReviewModuleEnabled() && checkGoalModuleEnabled()) return true;
      return false;
    },
    Comp: ChangeWeightageComp,
    hideOnProfilePage: ({
      empId,
      isLoggedInPersonManagerOfManagerOfProfilePerson,
      isProfileEmployeeDirectReportee,
    }) => {
      if (!checkGoalModuleEnabled()) return true;
      return !(
        checkUserIsSelf(empId) ||
        checkUserIsReviewAdmin() ||
        isLoggedInPersonManagerOfManagerOfProfilePerson ||
        isProfileEmployeeDirectReportee
      );
    },
  },
  {
    displayName: 'Add a 1:1',
    Icon: OneOnOne,
    isActionVisible: checkOneOnOneModuleEnabled,
    onClick: ({ dispatch, setIsOneOnOnePanelOpen = () => {} }) => {
      check1on1V2Enabled()
        ? dispatch(
            openCreateOneOnOneDrawer({
              excludedParticipantIds: [],
            })
          )
        : setIsOneOnOnePanelOpen(true);
    },
  },
  {
    displayName: 'Share public praise',
    Icon: Praise,
    isActionVisible: () => checkFeedbackModuleEnabled() && !checkDenyPublicCFVisibilityEnabled(),
    onClick: ({ history }) => {
      pushFeedbackParams({
        history,
        formType: FEEDBACK_FORM_TYPE.PROVIDED,
        newForm: true,
        empId: null,
        isSharePublicPraise: true,
        source: 'Quick Actions',
      });
    },
  },
  {
    displayName: 'Share feedback',
    Icon: ShareFeedback,
    isActionVisible: checkFeedbackModuleEnabled,
    onClick: ({ history }) => {
      pushFeedbackParams({
        history,
        formType: FEEDBACK_FORM_TYPE.PROVIDED,
        newForm: true,
        empId: null,
        source: 'Quick Actions',
      });
    },
  },
  {
    displayName: 'Request feedback',
    Icon: RequestFeedback,
    isActionVisible: checkFeedbackModuleEnabled,
    onClick: ({ history, employeeId, source = 'Quick Actions' }) => {
      pushFeedbackParams({
        history,
        formType: FEEDBACK_FORM_TYPE.REQUESTED,
        newForm: true,
        empId: employeeId ?? null,
        source,
      });
    },
  },

  {
    displayName: 'Add an Initiative',
    Icon: Initiatives,
    isActionVisible: () => {
      const accessPrivilegesList = store.getState().employeeGoalsAccessPrivileges ?? [];
      const canCreateInitiativePrivilege = accessPrivilegesList.includes(
        CREATE_INITIATIVE_PRIVILEGES.CREATE_INITIATIVES
      );
      return checkTaskModuleEnabled() && canCreateInitiativePrivilege;
    },
    onClick: ({ employee, dispatch }) => {
      const updatedToIds = [
        {
          uuid: employee.id,
          ownerType: 'OWNER',
          metadata: { displayName: employee.displayName },
          profile: { profilePhoto: employee.profilePhotoSrc },
        },
      ];

      dispatch(closeSecondaryPanel()); /// // WILL CLOSE SECONDARY DRAWER ON INITIAL OPENING ON ADD NEW INITIATIVE PANEL
      sendOpenedInitiativePanel({
        eventProperties: {
          page: 'Home',
          source: 'Quick Actions',
        },
      });
      dispatch(
        openPanel({
          toIds: updatedToIds,
          page: 'Home',
          source: 'Quick Actions',
        })
      );
    },
  },
  // {
  //   displayName: 'Share an experience',
  //   Icon: Engagement,
  //   isActionVisible: checkEngagementModuleEnabled,
  //   onClick: ({ setIsExperiencePanelOpen }) => {
  //     setIsExperiencePanelOpen(true);
  //   }
  // }
];
